import * as React from "react";
import { Form, message } from 'antd';
import { WrappedFormUtils, GetFieldDecoratorOptions, FormComponentProps } from "antd/lib/form/Form";
import { useGenericState } from "src/Library/UseGenericState";
import useS3Upload, { UploadState, S3File } from "../useS3Upload";
import { UploadFile } from "antd/lib/upload/interface";
import * as uuid from 'uuid';

export interface DocumentProps<T> {
  // key: string;
  // value?: any;
  disabled?: boolean;
  visible?: boolean;
  label?: string;
  // placeholder?: string;
  // formOptions?: GetFieldDecoratorOptions;
  extraData?: T;
  // [key: string]: any;
  multi?: boolean;
  accept?: string;
  isMulti?: boolean;
  initialPath?: string;
  initialFileList?: UploadFile[];
  onClickSampleDownload?: (payload: any) => Promise<void>;
};

export type Attachment = {
  name: string;
  url: string;
  status: string;
  uid: string;
}

export interface DocumentModel<T> extends UploadState{
  // key: string;
  disabled: boolean;
  visible: boolean;
  label?: string;
  // placeholder?: string;
  decorator: (child: React.ReactElement) => React.ReactNode;
  // value?: any;
  setValue: (val: any) => void;
  reset: () => void,
  enable: () => void,
  disable: () => void,
  show: () => void,
  hide: () => void,
  // setKey: (val: string) => void,
  setLabel: (val: string) => void,
  // setFormOptions: (formOptions: string) => void,
  // setPlaceholder: (val: string) => void,
  // formOptions?: GetFieldDecoratorOptions;
  extraData?: T;
  // isRequired: boolean;
  fieldType: string;
  value: UploadFile[];
  multi: boolean;
  isMulti?: boolean;
  setMulti: (val: boolean) => void;
  removeFile: (uid: string) => void;
};

export type useFieldProps<T> = DocumentProps<T> & FormComponentProps;

const useDocument = <T>({
  form,
  // key,
  // value,
  disabled = false,
  visible = true,
  label,
  // placeholder,
  // formOptions,
  extraData,
  multi,
  accept,
  isMulti,
  initialPath,
  initialFileList,
  onClickSampleDownload,
}: useFieldProps<T>
  ): DocumentModel<T> /*& { setValue: (val: Object) => void; }*/ => {
  const [state, setState] = useGenericState({
    // key,
    // value,
    // formOptions,
    disabled,
    visible,
    label,
    // placeholder,
    value: (initialFileList || []) as UploadFile[],
    multi: multi ?? false,
    isDownloading: false,
    isMulti: isMulti ?? false,

  });
  const s3Upload = useS3Upload(initialPath || 'shipment-execution');
  React.useEffect(() => {
    if (!s3Upload.uploadState.isUploading && s3Upload.uploadState.s3File.name) {
      setState({
        value: [
          ...state.value?.map(val => {
            if (val.name === s3Upload.uploadState.s3File.name && !val.url) {
              return {
                ...val,
                // name: s3Upload.uploadState.s3File.name,
                status: s3Upload.uploadState.s3File.s3Link ? 'done' : 'error',
                url: s3Upload.uploadState.s3File.s3Link,
              } as UploadFile;
            } else {
              return val;
            }
          }) || [],
        ]
      });
    }
  }, [s3Upload.uploadState.isUploading]);

  const handleCustomRequest = (file) => {
    if ((!state.multi && !state.value?.length) || state.multi) {
      setState({
        value: [
          ...state.value,
          {
            uid: uuid(),
            name: file.file.name,
            status: 'uploading',
            url: '',
          } as UploadFile,
        ]
      });
      s3Upload.uploadToS3(file.file);
    } else {
      message.error(`Multiple files can't be uploaded!`);
    }
  };
  const handleCustomMultiRequest = (files) => {
    // Ensure files is always an array
        setState({
      value: [
        ...state.value,
        {
          uid: uuid(),
          name: files.name,
          status: 'uploading',
          url: '',
        } as UploadFile,
      ]
    });
    s3Upload.uploadToS3(files);
  };
  
  const handleRemove = (file) => {
    setState({
      value: [
        ...state.value.filter(val => val.uid !== file.uid),
      ]
    });
  };

  const removeFile = (uid: string) => {
    setState({
      value: state.value.filter(val => val.uid !== uid),
    });
  };

  const handleDownloadFile = async () => {
    if (!onClickSampleDownload) return;
    if (state.isDownloading) return;
    setState({
      isDownloading: true,
    });
    const payload = {
    }
    await onClickSampleDownload(payload);
    setState({
      isDownloading: false,
    });
  };
  
  const decorator = (child: React.ReactElement): React.ReactNode => {
    return React.cloneElement(child, {
        ...child.props,
        fileList: state.value,
        disabled: state.disabled,
        visible: state.visible,
        customRequest: child.props.customRequest || (() => {}),
        onChange: (info) => {
          if (info.file.status === 'removed') return;
          if (isMulti) {
            handleCustomMultiRequest(info.file);
          } else {
            handleCustomRequest(info);
          }
        },
        isUploading: s3Upload.uploadState.isUploading,
        isDownloading: state.isDownloading,
        onRemove: handleRemove,
        // placeholder: state.placeholder,
        sampleFileDownload: onClickSampleDownload ? handleDownloadFile : undefined,
        accept,
    });
  };

  const enable = () => setState({ disabled: false });
  const disable = () => setState({ disabled: true });
  const show = () => setState({ visible: true });
  const hide = () => setState({ visible: false });
  // const setKey = (key) => setState({ key });
  // const setFormOptions = (formOptions) => setState({ formOptions });
  const setLabel = (label) => setState({ label });
  const setMulti = (multi: boolean) => setState({ multi });
  // const setPlaceholder = (placeholder) => setState({ placeholder });
  const reset = () => setState({
    value: [],
  });

  // const isRequired = !!state.formOptions?.rules?.some((rule) => rule.required);
  // const fieldType = 'document';

  const setValueHandler = (value) => setState({ value });
  return {
    // key,
    disabled: state.disabled,
    visible: state.visible,
    label: state.label,
    // placeholder: state.placeholder,
    // value: state.value,
    setValue: setValueHandler,
    reset,
    // setKey,
    setLabel,
    // setFormOptions,
    // setPlaceholder,
    decorator,
    enable,
    disable,
    show,
    hide,
    // formOptions,
    extraData,
    // isRequired,
    fieldType: 'document',
    value: state.value,
    multi: state.multi,
    setMulti,
    removeFile,
    ...s3Upload.uploadState,
  };
};

export default useDocument;
